import { useEffect, useState, useCallback, useRef } from "react";
import type { RefObject } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  sendDraftEmail,
  rememberEmailEdit,
  getDraftsFiltered,
  DraftFilters,
} from "./DataServices/EmailDraftService";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Loading from "./Loading";

/* ─── Types matching actual backend GET /api/email-drafts response ─── */
interface ApiDraft {
  id: string;
  tenantEmail: string;
  subject: string;
  draftBody: string;
  createdAt: string;
  listingId: string;
  inquiry: string; // JSON-serialized EmailDraftInput OR raw text
  threadSize: number;
}

/* Parsed shape used by UI */
interface DraftItem {
  id: string;
  tenantEmail: string;
  tenantName: string;
  subject: string;
  address: string;
  draftBody: string;
  createdAt: string;
  threadSize: number;
  inquiryBody: string; // The original email body / conversation
}

interface Filters {
  types: string[];
  showingStatus: string[];
  appfolioStatus: string[];
  threadSize: string[];
}

/* ─── Helpers ─── */

/** Try to parse inquiry JSON (EmailDraftInput) and extract useful fields */
function parseDraft(raw: ApiDraft): DraftItem {
  let inquiryBody = "";
  let tenantName = "";
  let address = "";

  try {
    const parsed = JSON.parse(raw.inquiry);
    // EmailDraftInput shape: { emailMeta, listingContext, ... }
    inquiryBody = parsed.emailMeta?.body || raw.inquiry;
    tenantName = extractNameFromEmail(
      parsed.emailMeta?.from || raw.tenantEmail,
    );
    address =
      parsed.listingContext?.apartment?.title ||
      parsed.listingContext?.apartment?.address ||
      "";
  } catch {
    // Not valid JSON – treat as raw text
    inquiryBody = raw.inquiry || "";
    tenantName = extractNameFromEmail(raw.tenantEmail);
  }

  return {
    id: raw.id,
    tenantEmail: raw.tenantEmail,
    tenantName,
    subject: raw.subject,
    address,
    draftBody: raw.draftBody,
    createdAt: raw.createdAt,
    threadSize: raw.threadSize,
    inquiryBody,
  };
}

/** Extract a display name from "Name <email>" or just an email address */
function extractNameFromEmail(emailOrHeader: string): string {
  if (!emailOrHeader) return "Unknown";
  // "John Doe <john@example.com>"
  const match = emailOrHeader.match(/^(.+?)\s*<[^>]+>$/);
  if (match) return match[1].trim();
  // plain email → use part before @
  const atIdx = emailOrHeader.indexOf("@");
  if (atIdx > 0) {
    return emailOrHeader
      .substring(0, atIdx)
      .replace(/[._-]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return emailOrHeader;
}

/** Build multi-value API filters from UI multi-select state. */
function buildApiFilters(ui: Filters): DraftFilters {
  const f: DraftFilters = {};

  if (ui.types.length > 0) f.types = ui.types;
  if (ui.showingStatus.length > 0) f.showingStatuses = ui.showingStatus;
  if (ui.appfolioStatus.length > 0) f.appfolioStatuses = ui.appfolioStatus;
  if (ui.threadSize.length > 0) f.threadSizes = ui.threadSize;

  return f;
}

/* ─── Reusable Panels for Layout ─── */

function FiltersPanel({
  filters,
  onToggle,
  className = "",
}: {
  filters: Filters;
  onToggle: (category: keyof Filters, value: string) => void;
  className?: string;
}) {
  return (
    <div className={className}>
      {/* Types of emails */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-gray-900 mb-2">
          Types of emails
        </h3>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.types.includes("streeteasy")}
              onChange={() => onToggle("types", "streeteasy")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.types.includes("streeteasy") ? "text-gray-900 font-medium" : "text-gray-600"}`}
            >
              Streeteasy
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.types.includes("queries")}
              onChange={() => onToggle("types", "queries")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.types.includes("queries") ? "text-gray-900 font-medium" : "text-gray-600"}`}
            >
              Queries
            </span>
          </label>
        </div>
      </div>

      {/* Showing status */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-gray-900 mb-2">
          Showing status
        </h3>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.showingStatus.includes("Inquired")}
              onChange={() => onToggle("showingStatus", "Inquired")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.showingStatus.includes("Inquired") ? "text-yellow-700 font-medium" : "text-gray-600"}`}
            >
              Inquired
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.showingStatus.includes("Confirmed")}
              onChange={() => onToggle("showingStatus", "Confirmed")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.showingStatus.includes("Confirmed") ? "text-blue-700 font-medium" : "text-gray-600"}`}
            >
              Confirmed
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.showingStatus.includes("Seen")}
              onChange={() => onToggle("showingStatus", "Seen")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.showingStatus.includes("Seen") ? "text-green-700 font-medium" : "text-gray-600"}`}
            >
              Seen
            </span>
          </label>
        </div>
      </div>

      {/* Application status */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-gray-900 mb-2">
          Application status
        </h3>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.appfolioStatus.includes("Not Started")}
              onChange={() => onToggle("appfolioStatus", "Not Started")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-gray-600 focus:ring-gray-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.appfolioStatus.includes("Not Started") ? "text-gray-900 font-medium" : "text-gray-600"}`}
            >
              Not started
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.appfolioStatus.includes("Partial")}
              onChange={() => onToggle("appfolioStatus", "Partial")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.appfolioStatus.includes("Partial") ? "text-orange-700 font-medium" : "text-gray-600"}`}
            >
              In process
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.appfolioStatus.includes("Completed")}
              onChange={() => onToggle("appfolioStatus", "Completed")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.appfolioStatus.includes("Completed") ? "text-green-700 font-medium" : "text-gray-600"}`}
            >
              Completed
            </span>
          </label>
        </div>
      </div>

      {/* Thread size */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-gray-900 mb-2">
          Thread size
        </h3>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.threadSize.includes("1-3")}
              onChange={() => onToggle("threadSize", "1-3")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.threadSize.includes("1-3") ? "text-gray-900 font-medium" : "text-gray-600"}`}
            >
              1-3
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.threadSize.includes("4-6")}
              onChange={() => onToggle("threadSize", "4-6")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.threadSize.includes("4-6") ? "text-gray-900 font-medium" : "text-gray-600"}`}
            >
              4-6
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.threadSize.includes("6+")}
              onChange={() => onToggle("threadSize", "6+")}
              className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-500 focus:ring-offset-0"
            />
            <span
              className={`text-xs group-hover:text-gray-900 ${filters.threadSize.includes("6+") ? "text-gray-900 font-medium" : "text-gray-600"}`}
            >
              6+
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

function EmailListPanel({
  drafts,
  selectedDraft,
  onSelectDraft,
  formatDate,
  className = "",
}: {
  drafts: DraftItem[];
  selectedDraft: DraftItem | null;
  onSelectDraft: (draft: DraftItem) => void;
  formatDate: (dateString: string) => string;
  className?: string;
}) {
  return (
    <div className={className}>
      {drafts.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-8">
          <svg
            className="w-10 h-10 text-gray-300 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <p className="text-xs">No email drafts</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              onClick={() => onSelectDraft(draft)}
              className={`mb-2 rounded-xl px-3 py-2.5 cursor-pointer transition shadow-sm ${
                selectedDraft?.id === draft.id
                  ? "bg-gray-200 shadow-inner"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              <div className="flex justify-between items-start mb-0.5">
                <span className="font-semibold text-xs text-gray-900 truncate">
                  {draft.tenantName}
                </span>
                <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">
                  {formatDate(draft.createdAt)}
                </span>
              </div>
              <div className="text-[10px] text-gray-500 truncate">
                {draft.tenantEmail}
              </div>
              <div className="text-[10px] text-gray-600 truncate mt-0.5">
                {draft.address || draft.subject}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailDetailPanel({
  selectedDraft,
  sending,
  remembering,
  replyExpanded,
  onToggleReplyExpanded,
  replyRef,
  editedSubject,
  onEditedSubjectChange,
  editedReply,
  onEditedReplyChange,
  onOpenNotesModal,
  onSend,
  onCancelSelection,
  showBack = false,
  onBack,
  className = "",
}: {
  selectedDraft: DraftItem | null;
  sending: boolean;
  remembering: boolean;
  replyExpanded: boolean;
  onToggleReplyExpanded: () => void;
  replyRef: RefObject<HTMLTextAreaElement>;
  editedSubject: string;
  onEditedSubjectChange: (value: string) => void;
  editedReply: string;
  onEditedReplyChange: (value: string) => void;
  onOpenNotesModal: () => void;
  onSend: () => void;
  onCancelSelection: () => void;
  showBack?: boolean;
  onBack?: () => void;
  className?: string;
}) {
  if (!selectedDraft) {
    return (
      <div className={className}>
        <div className="flex-1 flex items-center justify-center bg-white">
          <div className="text-center px-4">
            <svg
              className="w-12 h-12 text-gray-300 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <p className="text-gray-400 text-xs">
              Select an email draft to view
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Email History Header */}
      <div className="px-6 py-3 bg-white border-b border-gray-200 flex items-center gap-2">
        {showBack && onBack && (
          <button
            type="button"
            onClick={onBack}
            className="w-7 h-7 flex items-center justify-center bg-black text-white rounded-md hover:bg-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
        )}
        <h2 className="text-sm font-semibold text-gray-900">Emails history</h2>
      </div>

      {/* Email Messages (history) - scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 relative min-h-0">
        {/* Loading overlay */}
        {(sending || remembering) && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-xs text-gray-600">
                {sending ? "Sending email..." : "Processing..."}
              </p>
            </div>
          </div>
        )}

        {/* Original inquiry / conversation */}
        <div className="bg-white rounded border border-gray-200 p-4">
          <div className="text-[10px] text-gray-500 mb-2 flex justify-between">
            <span>
              <span className="font-medium">FROM:</span>{" "}
              {selectedDraft.tenantEmail}
            </span>
            <span>{new Date(selectedDraft.createdAt).toUTCString()}</span>
          </div>
          <div className="text-[10px] text-gray-500 mb-2">
            <span className="font-medium">SUBJECT:</span>{" "}
            {selectedDraft.subject}
          </div>

          <div className="text-[11px] text-gray-800 whitespace-pre-wrap leading-relaxed">
            {selectedDraft.inquiryBody}
          </div>
        </div>
      </div>

      {/* Draft Reply - below history, no own scrollbar */}
      <div className="bg-white border-t border-gray-100 px-6 py-3">
        <button
          type="button"
          onClick={onToggleReplyExpanded}
          className="w-full flex items-center justify-between text-[11px] text-gray-700 font-medium mb-2 cursor-pointer px-3 py-1.5 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          <span>Draft reply</span>
          <span className="text-gray-400 text-[9px]">
            {replyExpanded ? "Hide" : "Show"}
          </span>
        </button>

        {replyExpanded && (
          <>
            <div className="mb-2 mt-2">
              <input
                type="text"
                value={editedSubject}
                onChange={(e) => onEditedSubjectChange(e.target.value)}
                disabled={sending || remembering}
                className="w-full px-2 py-1 bg-white border border-gray-200 rounded text-[11px] text-gray-900 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 focus:outline-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Subject"
              />
            </div>
            <textarea
              ref={replyRef}
              value={editedReply}
              onChange={(e) => onEditedReplyChange(e.target.value)}
              disabled={sending || remembering}
              rows={7}
              className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded text-[11px] text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 focus:outline-none resize-none disabled:bg-gray-50 disabled:cursor-not-allowed leading-tight"
              placeholder="Type your reply here..."
            />
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
        <button
          onClick={onCancelSelection}
          disabled={sending || remembering}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white text-xs font-medium rounded hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Cancel
        </button>

        <button
          onClick={onOpenNotesModal}
          disabled={remembering || sending}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-red-300 text-red-500 text-xs font-medium rounded hover:bg-red-50 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
        >
          {remembering ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              AI learning
            </>
          )}
        </button>

        <button
          onClick={onSend}
          disabled={sending || remembering}
          className="inline-flex items-center gap-1.5 px-5 py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {sending ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            "Send"
          )}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════ Component ═══════════════ */

export default function EmailInbox() {
  const { listingId, apartmentName } = useParams();
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<DraftItem | null>(null);
  const [editedReply, setEditedReply] = useState("");
  const [replyExpanded, setReplyExpanded] = useState(false);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const [editedSubject, setEditedSubject] = useState("");

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [remembering, setRemembering] = useState(false);

  // AI Learning Modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [aiLearningNotes, setAiLearningNotes] = useState("");
  const [learningResult, setLearningResult] = useState<{
    skipped: boolean;
    message: string;
    differenceSummary?: string | null;
    skipReason?: string;
  } | null>(null);

  // Mobile layout controls
  const [mobileView, setMobileView] = useState<"list" | "detail">("list");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState<Filters>({
    types: [],
    showingStatus: [],
    appfolioStatus: [],
    threadSize: [],
  });

  /* ── Fetch drafts from API ── */
  const loadDrafts = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      const apiFilters = buildApiFilters(filters);
      const raw: ApiDraft[] =
        (await getDraftsFiltered(listingId, apiFilters)) || [];
      setDrafts(raw.map(parseDraft));
    } catch (error) {
      console.error("Failed to load drafts:", error);
    } finally {
      setLoading(false);
    }
  }, [listingId, filters]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  /* ── Filter toggle ── */
  const handleFilterToggle = (category: keyof Filters, value: string) => {
    setFilters((prev) => {
      const current = prev[category];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
    setSelectedDraft(null);
    setMobileView("list");
  };

  /* ── Select a draft ── */
  const handleSelectDraft = (draft: DraftItem) => {
    setSelectedDraft(draft);
    setEditedReply(draft.draftBody || "");
    setEditedSubject(draft.subject || "");
    setAiLearningNotes("");
    setReplyExpanded(false);
    setMobileView("detail");
    setMobileFiltersOpen(false);
  };

  /* ── Send ── */
  const handleSend = async () => {
    if (!selectedDraft) return;
    setSending(true);
    try {
      await sendDraftEmail(selectedDraft.id, editedSubject, editedReply);
      setDrafts((prev) => prev.filter((d) => d.id !== selectedDraft.id));
      setSelectedDraft(null);
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setSending(false);
    }
  };

  /* ── AI Learning (remember) ── */
  const handleAILearning = async () => {
    if (!selectedDraft) return;
    setRemembering(true);
    try {
      const result = await rememberEmailEdit(
        selectedDraft.tenantEmail,
        listingId,
        editedSubject,
        editedReply,
        selectedDraft.draftBody,
        aiLearningNotes,
      );
      setLearningResult(
        result ?? {
          skipped: false,
          message: "Email edit remembered successfully",
        },
      );
      setAiLearningNotes("");
    } catch (error) {
      console.error("Failed to save AI learning:", error);
      setLearningResult({
        skipped: false,
        message: "An error occurred while saving. Please try again.",
      });
    } finally {
      setRemembering(false);
    }
  };

  const openNotesModal = () => {
    setLearningResult(null);
    setShowNotesModal(true);
  };
  const closeNotesModal = () => {
    setShowNotesModal(false);
    setAiLearningNotes("");
    setLearningResult(null);
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  };

  if (loading) return <Loading />;

  return (
    <div className="h-screen bg-[#f3f3f3] flex flex-col">
      {/* AI LEARNING NOTES MODAL */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {learningResult ? (
              /* ── Result view ── */
              <>
                <div className="px-5 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    AI Learning Result
                  </h3>
                </div>
                <div className="px-5 py-4">
                  <div
                    className={`flex items-start gap-3 rounded-lg p-3 ${
                      learningResult.skipped
                        ? "bg-yellow-50 border border-yellow-200"
                        : "bg-green-50 border border-green-200"
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">
                      {learningResult.skipped ? "⚠️" : "✅"}
                    </span>
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-semibold ${
                          learningResult.skipped
                            ? "text-yellow-800"
                            : "text-green-800"
                        }`}
                      >
                        {learningResult.skipped
                          ? "Learning Skipped"
                          : "Learning Saved!"}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${
                          learningResult.skipped
                            ? "text-yellow-700"
                            : "text-green-700"
                        }`}
                      >
                        {learningResult.message}
                      </p>

                      {learningResult.skipped &&
                        learningResult.skipReason === "no_changes" && (
                          <p className="text-[10px] text-yellow-600 mt-1 italic">
                            The draft was not modified from the original.
                          </p>
                        )}
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={closeNotesModal}
                    className="px-4 py-1.5 text-xs font-medium text-white bg-gray-800 rounded hover:bg-gray-900"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              /* ── Input view ── */
              <>
                <div className="px-5 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    AI Learning Notes
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    What changes did you make to the draft?
                  </p>
                </div>
                <div className="px-5 py-3">
                  <textarea
                    value={aiLearningNotes}
                    onChange={(e) => setAiLearningNotes(e.target.value)}
                    rows={3}
                    autoFocus
                    className="w-full px-2.5 py-2 border border-gray-300 rounded text-xs text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 focus:outline-none resize-none"
                    placeholder="E.g., Changed pricing info, added pet policy details..."
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Adding notes is optional and up to you. If no changes are
                    detected by AI, you can still add notes to save context and
                    learning.
                  </p>
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
                  <button
                    onClick={closeNotesModal}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAILearning}
                    disabled={remembering}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 disabled:bg-gray-400 inline-flex items-center gap-1.5"
                  >
                    {remembering ? (
                      <>
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      "Save Learning"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-[#f3f3f3] border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="w-7 h-7 flex items-center justify-center bg-black text-white rounded-md hover:bg-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <h1 className="text-base font-medium text-black">
            <span className="text-red-500">AI inbox</span> for apartment{" "}
            {apartmentName || "#403"}
          </h1>
        </div>
      </div>

      {/* MOBILE LAYOUT: filters toggle + two-page (list/detail) */}
      <div className="flex-1 flex flex-col md:hidden overflow-hidden">
        <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-[11px] font-medium text-gray-800 shadow-sm hover:bg-gray-50"
          >
            Filters
          </button>

          {selectedDraft && (
            <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden text-[11px]">
              <button
                type="button"
                onClick={() => setMobileView("list")}
                className={`px-3 py-1.5 font-medium ${
                  mobileView === "list"
                    ? "bg-gray-900 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                All emails
              </button>
              <button
                type="button"
                onClick={() => setMobileView("detail")}
                className={`px-3 py-1.5 font-medium border-l border-gray-300 ${
                  mobileView === "detail"
                    ? "bg-gray-900 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Selected email
              </button>
            </div>
          )}
        </div>

        {mobileFiltersOpen && (
          <div className="px-3 pb-2">
            <FiltersPanel
              filters={filters}
              onToggle={handleFilterToggle}
              className="w-full bg-white rounded-2xl shadow-sm border border-gray-200 py-4 px-4 max-h-64 overflow-y-auto"
            />
          </div>
        )}

        <div className="flex-1 flex flex-col px-3 pb-4 overflow-hidden">
          {mobileView === "list" && (
            <EmailListPanel
              drafts={drafts}
              selectedDraft={selectedDraft}
              onSelectDraft={handleSelectDraft}
              formatDate={formatDate}
              className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden"
            />
          )}

          {mobileView === "detail" && (
            <EmailDetailPanel
              selectedDraft={selectedDraft}
              sending={sending}
              remembering={remembering}
              replyExpanded={replyExpanded}
              onToggleReplyExpanded={() => setReplyExpanded((prev) => !prev)}
              replyRef={replyRef}
              editedSubject={editedSubject}
              onEditedSubjectChange={(value) => setEditedSubject(value)}
              editedReply={editedReply}
              onEditedReplyChange={(value) => setEditedReply(value)}
              onOpenNotesModal={openNotesModal}
              onSend={handleSend}
              onCancelSelection={() => setSelectedDraft(null)}
              showBack
              onBack={() => setMobileView("list")}
              className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-0"
            />
          )}
        </div>
      </div>

      {/* DESKTOP / TABLET LAYOUT - 3 Column */}
      <div className="hidden md:flex flex-1 gap-4 px-4 pb-4 pt-3 overflow-hidden min-h-0">
        {/* LEFT SIDEBAR - FILTERS */}
        <FiltersPanel
          filters={filters}
          onToggle={handleFilterToggle}
          className="w-48 bg-white rounded-2xl shadow-sm border border-gray-200 py-5 px-5 overflow-y-auto flex-shrink-0"
        />

        {/* MIDDLE PANEL - EMAIL LIST */}
        <EmailListPanel
          drafts={drafts}
          selectedDraft={selectedDraft}
          onSelectDraft={handleSelectDraft}
          formatDate={formatDate}
          className="w-72 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden flex-shrink-0"
        />

        {/* RIGHT PANEL - EMAIL HISTORY & REPLY */}
        <EmailDetailPanel
          selectedDraft={selectedDraft}
          sending={sending}
          remembering={remembering}
          replyExpanded={replyExpanded}
          onToggleReplyExpanded={() => setReplyExpanded((prev) => !prev)}
          replyRef={replyRef}
          editedSubject={editedSubject}
          onEditedSubjectChange={(value) => setEditedSubject(value)}
          editedReply={editedReply}
          onEditedReplyChange={(value) => setEditedReply(value)}
          onOpenNotesModal={openNotesModal}
          onSend={handleSend}
          onCancelSelection={() => setSelectedDraft(null)}
          className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-0"
        />
      </div>
    </div>
  );
}
